<template>
  <div class="card">
    <b-card-header v-if="$hasPermission('manage_users')">
      <b-button
        v-b-modal.modal-add-user
        variant="success"
        @click="addNewUser"
      >
        {{ $t('usersettings.addUser') }}
      </b-button>
    </b-card-header>

    <div class="table-responsive mb-0">
      <table class="table b-table table-striped table-hover">
        <thead>
          <!--          <tr>-->
          <!--            <th-->
          <!--              style="cursor: pointer;"-->
          <!--              @click="sortCol('name')"-->
          <!--            >-->
          <!--              Name-->
          <!--            </th>-->
          <!--            <th>Email</th>-->
          <!--            <th>Role</th>-->
          <!--            <th>Department</th>-->
          <!--            <th>Status</th>-->
          <!--            <th>Action</th>-->
          <!--          </tr>-->
          <tr>
            <th
              style="cursor: pointer;"
              @click="sortCol('name')"
            >
              {{ $t('usersettings.name') }}
              <feather-icon
                v-if="sortBy === 'name'"
                :icon="sortDirection === 'asc' ? 'ChevronUpIcon' : 'ChevronDownIcon'"
              />
            </th>
            <th
              style="cursor: pointer;"
              @click="sortCol('email')"
            >
              {{ $t('pages.email') }}
              <feather-icon
                v-if="sortBy === 'email'"
                :icon="sortDirection === 'asc' ? 'ChevronUpIcon' : 'ChevronDownIcon'"
              />
            </th>
            <th
              style="cursor: pointer;"
              @click="sortCol('role_name')"
            >
              {{ $t('usersettings.role') }}
              <feather-icon
                v-if="sortBy === 'role_name'"
                :icon="sortDirection === 'asc' ? 'ChevronUpIcon' : 'ChevronDownIcon'"
              />
            </th>
            <th
              style="cursor: pointer;"
              @click="sortCol('department')"
            >
              {{ $t('usersettings.department') }}
              <feather-icon
                v-if="sortBy === 'department'"
                :icon="sortDirection === 'asc' ? 'ChevronUpIcon' : 'ChevronDownIcon'"
              />
            </th>
            <th
              style="cursor: pointer;"
              @click="sortCol('status')"
            >
              {{ $t('apps.status') }}
              <feather-icon
                v-if="sortBy === 'status'"
                :icon="sortDirection === 'asc' ? 'ChevronUpIcon' : 'ChevronDownIcon'"
              />
            </th>
            <th>
              {{ $t('apps.action') }}
            </th>
          </tr>
        </thead>
        <tbody v-if="!isBusy">
          <tr
            v-for="user in sortedItems"
            :key="user.id"
          >
            <td>
              <div class="font-weight-bold">
                {{ user.name }}
              </div>
              <b-badge
                v-if="user.is_owner"
                variant="light-primary"
              >
                {{ $t('usersettings.owner') }}
              </b-badge>
            </td>
            <td>{{ user.email }}</td>
            <td><b-badge variant="light-info">
              {{ user.role_name }}
            </b-badge></td>

            <td>{{ getDepartmentLabel(user.department) }}</td>
            <td>
              <b-badge :variant="getStatusVariant(user.status)">
                {{ getStatusLabel(user.status) }}
              </b-badge>
            </td>
            <td>
              <template v-if="$hasPermission('manage_users')">
                <b-button
                  size="sm"
                  variant="primary"
                  @click="editUser(user)"
                >
                  {{ $t('apps.edit') }}
                </b-button>
                <b-button
                  v-if="!user.is_owner && user.id !== currentUserId"
                  size="sm"
                  variant="danger"
                  class="ml-50"
                  @click="deleteUser(user)"
                >
                  {{ $t('usersettings.delete') }}
                </b-button>
              </template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <b-modal
      id="modal-add-user"
      scrollable
      hide-footer
      size="md"
      header-class="border-0 pb-0"
      @show="modalSessionKey += 1"
    >
      <add-user-modal
        :key="`${modalSessionKey}-${isEdit}-${selectedUser.id || 'new'}`"
        :user-data="selectedUser"
        :is-edit="isEdit"
        @saved="handleUserSaved"
      />
    </b-modal>
  </div>
</template>

<script>
import i18n from '@/libs/i18n'
import { systemLabel } from '@/libs/i18n/display'

import AddUserModal from './AddUserModal.vue'

export default {
  components: { AddUserModal },
  props: ['profile'],
  data() {
    return {
      isBusy: false,
      items: [],
      sortBy: 'name',
      sortDirection: 'asc',
      selectedUser: {},
      isEdit: false,
      modalSessionKey: 0,
    }
  },
  computed: {
    sortedItems() {
      return [...this.items].sort((a, b) => {
        // 1. Always keep the Owner at the very top
        if (a.is_owner && !b.is_owner) return -1
        if (!a.is_owner && b.is_owner) return 1

        // 2. Standard sorting for everyone else
        const modifier = this.sortDirection === 'asc' ? 1 : -1

        // Handle nulls or undefined to prevent crashes
        const valA = (a[this.sortBy] ?? '').toString().toLowerCase()
        const valB = (b[this.sortBy] ?? '').toString().toLowerCase()

        if (valA < valB) return -1 * modifier
        if (valA > valB) return 1 * modifier
        return 0
      })
    },
    currentUserId() {
      return this.profile?.id || null
    },
  },
  created() { this.fetchUsers() },
  methods: {
    fetchUsers() {
      this.isBusy = true
      this.$http.get('/api/publisher-users').then(res => {
        this.items = res.data
      })
        .catch(errors => {
          this.items = []
          this.alertApiError(errors, i18n.t('usersettings.failedToLoadUsers'))
        })
        .finally(() => {
          this.isBusy = false
        })
    },
    addNewUser() {
      this.isEdit = false
      this.selectedUser = {}
    },
    editUser(user) {
      this.isEdit = true
      this.selectedUser = { ...user, role: user.role_id } // Map role_id for the dropdown
      this.$bvModal.show('modal-add-user')
    },
    deleteUser(user) {
      // Use the full options object for msgBoxConfirm
      this.$bvModal.msgBoxConfirm(
        i18n.t('usersettings.areYouSureYouWantToDeleteTheUser', { value1: user.name }),
        {
          title: i18n.t('usersettings.deleteUserTitle', { value1: user.name }),
          size: 'md',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: i18n.t('usersettings.deleteUser'),
          cancelTitle: i18n.t('usersettings.cancel'),
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true,
        },
      ).then(value => {
        if (value) {
          this.showLoading()
          this.$http.delete(`/api/publisher-users/${user.id}`)
            .then(() => {
              this.fetchUsers()
              this.alertSuccess(i18n.t('usersettings.userDeletedSuccessfully'))
            })
            .catch(errors => {
              this.alertApiError(errors, i18n.t('usersettings.userDeleteFailed'))
            })
            .finally(() => {
              this.hideLoading()
            })
        }
      })
    },
    handleUserSaved(updatedUser) {
      this.$bvModal.hide('modal-add-user')
      this.fetchUsers()

      // 1. Check if the user we just saved is ME
      const currentStoreUser = this.$store.getters['auth/appActiveUser']

      if (updatedUser && currentStoreUser && updatedUser.id === currentStoreUser.id) {
        this.$store.dispatch('auth/fetchCurrentUser')
      }

      this.$emit('refresh-profile')
    },
    getDepartmentLabel(val) {
      const labs = {
        0: 'Business', 1: 'Tech', 2: 'Finance', 3: 'Other',
      }
      return systemLabel(labs[val] || 'Other')
    },
    getStatusLabel(status) {
      const labels = { 0: 'Inactive', 1: 'Active', 2: 'Pending' }
      return systemLabel(labels[status] || 'Inactive')
    },
    getStatusVariant(status) {
      const variants = { 0: 'light-danger', 1: 'light-success', 2: 'light-warning' }
      return variants[status] || 'light-danger'
    },
    sortCol(col) {
      if (this.sortBy === col) {
        // Toggle direction if clicking same column
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
      } else {
        // New column clicked: default to asc
        this.sortBy = col
        this.sortDirection = 'asc'
      }
    },
  },
}
</script>
